import React from "react";
import {useTranslation} from "react-i18next";
import styled from "styled-components";
import type {PaymentLinkOffer} from "services/WebCheckoutService";
import {ws} from "styles/tokens";
import {
  AccentTextButton,
  CardSubtitle,
  CardTitle,
  CtaArrowIcon,
  GradientCtaButton,
  OnboardingCard
} from "./onboardingStyles";

const OfferBox = styled.div`
  width: 100%;
  padding: 16px 18px;
  border-radius: ${ws.radiusLg};
  background: ${ws.wash};
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
`;

const OfferPlan = styled.strong`
  font-size: 18px;
  color: ${ws.fg};
`;

const OfferLine = styled.span`
  font-size: 15px;
  color: ${ws.muted};
`;

const Steps = styled.ol`
  width: 100%;
  margin: 0;
  padding-left: 22px;
  text-align: left;
  color: ${ws.fg};
  font-size: 15px;
  line-height: 1.6;
`;

const Notice = styled.p`
  margin: 0;
  font-size: 15px;
  color: ${ws.danger};
`;

interface Props {
  offer: PaymentLinkOffer;
  /** "intro" explains the steps before signing in; "pay" is the last step before Paddle's checkout. */
  mode: "intro" | "pay";
  /** The signed-in account's email, when someone is already signed in on the intro. */
  signedInEmail?: string | null;
  isSubmitting?: boolean;
  /** A reason the payment cannot go ahead, shown instead of the button. */
  error?: string | null;
  onContinue: () => void;
  onUseAnotherAccount?: () => void;
}

/** The offer a payment link carries, with the steps to take it. */
function PaymentLinkOfferStep({
  offer,
  mode,
  signedInEmail,
  isSubmitting,
  error,
  onContinue,
  onUseAnotherAccount
}: Props) {
  const {t} = useTranslation();
  const plan = `Waterspeed ${offer.tier === "ultra" ? t("subscribe.tierUltra") : t("subscribe.tierPro")} · ${
    offer.plan === "yearly" ? t("subscribe.planYearly") : t("subscribe.planMonthly")
  }`;
  const discount = offer.discount;
  const discountLine = discount
    ? `${
        discount.type === "percentage"
          ? t("payLink.offerPercentOff", {amount: discount.amount})
          : t("payLink.offerAmountOff", {amount: discount.amount.toFixed(2)})
      } ${
        discount.payments === 1
          ? t("payLink.offerFirstPayment")
          : discount.payments > 1
            ? t("payLink.offerFirstPayments", {count: discount.payments})
            : t("payLink.offerEveryPayment")
      }`
    : null;
  const continueLabel = mode === "pay" || signedInEmail ? t("payLink.continueToPayment") : t("payLink.getStarted");

  return (
    <OnboardingCard>
      <CardTitle>{t("payLink.offerTitle")}</CardTitle>
      <OfferBox>
        <OfferPlan>{plan}</OfferPlan>
        {offer.trial && <OfferLine>{t("payLink.offerTrial")}</OfferLine>}
        {discountLine && <OfferLine>{discountLine}</OfferLine>}
      </OfferBox>
      {mode === "intro" && !signedInEmail && (
        <Steps>
          <li>{t("payLink.stepAccount")}</li>
          <li>{t("payLink.stepPay")}</li>
          <li>{t("payLink.stepEnjoy")}</li>
        </Steps>
      )}
      {mode === "intro" && signedInEmail && (
        <CardSubtitle style={{overflowWrap: "anywhere"}}>
          {t("payLink.signedInAs", {email: signedInEmail})}
        </CardSubtitle>
      )}
      {mode === "pay" && <CardSubtitle>{t("payLink.payText")}</CardSubtitle>}
      {error ? (
        <Notice>{error}</Notice>
      ) : (
        <GradientCtaButton disabled={isSubmitting} onClick={onContinue}>
          {continueLabel}
          <CtaArrowIcon src="/assets/images/onboarding/arrow-cta.svg" alt="" />
        </GradientCtaButton>
      )}
      {mode === "intro" && signedInEmail && onUseAnotherAccount && (
        <AccentTextButton onClick={onUseAnotherAccount}>{t("payLink.useAnotherAccount")}</AccentTextButton>
      )}
    </OnboardingCard>
  );
}

export default PaymentLinkOfferStep;
